import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { IconButton, makeStyles, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import EditIcon from '@material-ui/icons/Edit'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AdminPage from '../../../admin/shared/AdminPage'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { smartEndpoints } from '../../../../../api/smartEndpoints'
import PolicyManagement from './PolicyManagement'

function formatRelativeTime (dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(mins / 60)
  const days = Math.floor(hours / 24)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

const useStyles = makeStyles(() => ({
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 2px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-sm) var(--border-radius-sm) 0 0',
    marginBottom: 'var(--margin-md)'
  },
  tab: {
    padding: 'var(--padding-sm) var(--padding-md)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 500,
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    borderBottom: '2px solid transparent',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    userSelect: 'none',
    '&:hover': {
      color: 'var(--color-neutral-700)'
    }
  },
  tabActive: {
    color: 'var(--color-primary-700)',
    borderBottomColor: 'var(--color-primary-500)',
    fontWeight: 600
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': {
      backgroundColor: 'var(--color-neutral-050)'
    },
    '& .operational-table > tbody > tr > td': {
      padding: '10px 18px 10px 18px'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  endpointCount: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  versionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  modeBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    lineHeight: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  modeRead: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  modeWrite: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  editBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  duplicateBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    '&:hover': {
      color: 'var(--color-neutral-600)',
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)'
  }
}))

const TABS = [
  { id: 'endpoints', label: 'Smart Endpoints' },
  { id: 'policies', label: 'Policies' }
]

function SmartEndpointsHome ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState('endpoints')
  const [searchText, setSearchText] = useState('')
  const [toast, setToast] = useState(null)

  const { data: programs, isLoading } = smartEndpoints.useListPrograms()

  const handleDuplicate = useCallback((program) => {
    const baseName = program.displayName ? `${program.displayName} (copy)` : ''
    const params = new URLSearchParams({
      duplicateFrom: program.code,
      name: baseName
    })
    history.push(`${root}/analysis/smart-endpoints/new?${params.toString()}`)
    setToast({ message: `Duplicating "${program.code}" — fill in a new code and save`, severity: 'info' })
  }, [history, root])

  const tableData = useMemo(() => {
    if (!programs) return []
    return programs.map(p => {
      const config = p.config || {}
      return {
        ...p,
        mode: config.mode === 'command' ? 'command' : 'query',
        lastModified: p.modifiedDate || p.createdDate || null,
        lastModifiedBy: p.modifiedBy || p.createdBy || null
      }
    })
  }, [programs])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'code',
      accessor: 'code',
      Header: 'Code',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'displayName', accessor: 'displayName', Header: 'Name' },
    {
      id: 'version',
      accessor: 'version',
      Header: 'Version',
      Cell: ({ value }) => <span className={classes.versionBadge}>v{value}</span>
    },
    {
      id: 'mode',
      accessor: 'mode',
      Header: 'Mode',
      Cell: ({ value }) => (
        <span className={`${classes.modeBadge} ${value === 'command' ? classes.modeWrite : classes.modeRead}`}>
          {value === 'command' ? 'read/write' : 'read only'}
        </span>
      )
    },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <span className={classes.timestampText} title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`}>
            {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
          </span>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton
            size='small'
            className={classes.duplicateBtn}
            onClick={() => handleDuplicate(row.original)}
            title='Duplicate endpoint'
          >
            <FileCopyIcon fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            className={classes.editBtn}
            onClick={() => history.push(`${root}/analysis/smart-endpoints/${row.original.code}`)}
            title='Edit endpoint'
          >
            <EditIcon fontSize='small' />
          </IconButton>
        </div>
      )
    }
  ], [classes, history, root, handleDuplicate])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'code', desc: false }], [])

  return (
    <AdminPage>
      <div className={classes.tabBar} role='tablist'>
        {TABS.map(tab => (
          <div
            key={tab.id}
            role='tab'
            aria-selected={activeTab === tab.id}
            className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
        <div style={{ marginLeft: 'auto', padding: '6px 0' }}>
          {activeTab === 'endpoints' && (
            <Link to={`${root}/analysis/smart-endpoints/new`}>
              <SydButton size='sm' variant='primary' icon='add'>New</SydButton>
            </Link>
          )}
        </div>
      </div>

      {activeTab === 'endpoints' && (
        isLoading
          ? <Loading />
          : tableData.length === 0
            ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--text-size-sm)', color: 'var(--color-neutral-500)', marginBottom: '24px', lineHeight: 1.7 }}>
                  No smart endpoints yet. Create one to get started.
                </div>
                <Link to={`${root}/analysis/smart-endpoints/new`}>
                  <SydButton size='sm' variant='primary' icon='add'>New Smart Endpoint</SydButton>
                </Link>
              </div>
            )
            : (
              <>
                <div className={classes.toolbar}>
                  <span className={classes.endpointCount}>
                    {tableData.length} {tableData.length === 1 ? 'endpoint' : 'endpoints'}
                  </span>
                  <input
                    className={classes.searchInput}
                    placeholder='Search endpoints…'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className={classes.tableWrap}>
                  <OperationalTable
                    variant='blank'
                    itemName='Smart Endpoints'
                    columns={columns}
                    data={tableData}
                    defaultSort={defaultSort}
                    searchText={searchText}
                  />
                </div>
              </>
            )
      )}

      {activeTab === 'policies' && (
        <PolicyManagement />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </AdminPage>
  )
}

SmartEndpointsHome.propTypes = {
  root: PropTypes.string
}

export default SmartEndpointsHome
